<script lang="ts" setup>
import { Head, Link, router, usePage } from "@inertiajs/vue3";
import HeadingComponent from "../../Components/HeadingComponent.vue";
import EventPublicWrapper from "../EventPublic/EventPublicWrapper.vue";
import { nextTick, onMounted, onUnmounted, reactive, ref } from "vue";
import { PencilIcon } from "@heroicons/vue/20/solid";

import { Event } from "@/types";
import DialogModal from "@/Components/DialogModal.vue";
import JetLabel from "@/Components/InputLabel.vue";
import JetButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import "/node_modules/flag-icons/css/flag-icons.min.css";
import eventBus from "@/Helpers/eventBus";
import TooltipIcon from "@/Components/TooltipIcon.vue";
import LocalEventTime from "@/Components/LocalEventTime.vue";
import { DateTime } from "luxon";
import Button from "@/Components/Button.vue";
import FormInput from "../../Components/FormInput.vue";

interface Props {
    event: Event;
    canRegisterForEvent: boolean;
}

const competitorState = ref(null);
const competitorStateReason = ref("");
const startTimeModalInputValue = ref("");
const props = withDefaults(defineProps<Props>(), {});

const startListClassSelected = ref(null);
const startListSectionSelected = ref(null);
const isCompetitorFormModalLoading = ref(false);
const startOrderType = ref("by_competitor_numbers");

function getFormattedPlannedStartTime(section, competitorClassSection) {
    if (section.start_type === "rolling_window") {
        if (!competitorClassSection.planned_start_at) {
            return "-";
        }

        const windowFrom = DateTime.fromISO(competitorClassSection.planned_start_at)
            .setZone(usePage().props.event.timezone)
            .toLocaleString(DateTime.DATETIME_SHORT);

        const windowTo = DateTime.fromISO(competitorClassSection.planned_start_at)
            .plus({ minutes: 60 })
            .setZone(usePage().props.event.timezone)
            .toLocaleString(DateTime.TIME_24_SIMPLE);

        return `${windowFrom} - ${windowTo}`;
    }

    if (section.start_type === "rolling_open") {
        if (!competitorClassSection.start_at) {
            return "-";
        }
        return DateTime.fromISO(competitorClassSection.start_at)
            .setZone(usePage().props.event.timezone)
            .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS);
    }

    if (!competitorClassSection.planned_start_at) {
        return "-";
    }

    // standing
    return DateTime.fromISO(competitorClassSection.planned_start_at)
        .setZone(usePage().props.event.timezone)
        .toLocaleString(DateTime.TIME_24_SIMPLE);
}

function getSections(eventClass, startListSectionSelected) {
    return startListSectionSelected !== null
        ? eventClass.classSections.filter((cs) => cs.id === startListSectionSelected)
        : eventClass.classSections;
}

function getCompetitorsInClass(startListClassSelected) {
    return startListClassSelected !== null
        ? usePage().props.event.classes.filter((c) => c.id === startListClassSelected)
        : usePage().props.event.classes;
}

function editCompetitor(competitor) {
    // console.log("competitor", competitor);
    isCompetitorFormModalLoading.value = true;
    eventBus.$emit("showCompetitorFormModal", {
        type: "update",
        competitor: competitor,
    });
}

const modalStartTimes = reactive({
    show: false,
});
const modalIndividualStartTime = reactive({
    show: false,
});

function showSetStartTimesModal() {
    modalStartTimes.show = true;
}

function regenerateStartTimesForSingleClassSection(starting_interval) {
    router.post(
        route("regenerateStartTimesForSingleClassSection", { event: props.event.id }),
        {
            generatePlannedStartTimeSectionId: generatePlannedStartTimeSectionId.value,
            starting_interval,
            start_order_type: startOrderType.value,
        },
        {
            onFinish: (visit) => {
                closeModals();
                generatePlannedStartTimeSectionId.value = null;
            },
        },
    );
}

function closeModals() {
    //console.log(arguments);
    modalStartTimes.show = false;
    modalIndividualStartTime.show = false;
}

function showModalIndividualStartTime(section, competitorClassSection) {
    if (!(usePage().props.can.update_event && section.start_type !== "rolling_open")) {
        return false;
    }

    modalIndividualStartTime.show = true;
    modalIndividualStartTime.competitorClassSection = competitorClassSection;
    modalIndividualStartTime.section = section;

    competitorState.value = competitorClassSection.state;
    competitorStateReason.value = competitorClassSection.state_reason;

    if (competitorClassSection.planned_start_at) {
        nextTick().then(() => {
            document.getElementById("datetimeIndividualStartTime").value = toEventLocalISOString(
                competitorClassSection.planned_start_at,
                props.event.timezone,
            );
            startTimeModalInputValue.value = toEventLocalISOString(
                competitorClassSection.planned_start_at,
                props.event.timezone,
            );
        });
    } else {
        setStartCompetitorAtToNextMinute();
    }
}

function setStartCompetitorAtToNextMinute() {
    console.log("device_ts_ms_offset", device_ts_ms_offset.value);
    const localRallyDateTime = DateTime.now()
        .minus(device_ts_ms_offset.value || 0)
        .plus(1 * 60000)
        .setZone(props.event.timezone);

    nextTick().then(() => {
        // console.log(new Date(new Date().getTime() + 1*60000));
        document.getElementById("datetimeIndividualStartTime").value =
            localRallyDateTime.toFormat("y-LL-dd'T'T");
        startTimeModalInputValue.value = localRallyDateTime.toFormat("y-LL-dd'T'T");
    });
}

function setIndividualStartTime(datetimeIndividualStartTime) {
    router.post(
        route("setIndividualStartTime", {
            competitorClassSection: modalIndividualStartTime.competitorClassSection.id,
        }),
        {
            datetimeIndividualStartTime,
        },
        {
            preserveScroll: true,
            onFinish: (visit) => {
                closeModals();
            },
        },
    );
}

function saveCompetitorState() {
    router.post(
        route("saveCompetitorState", {
            competitorClassSection: modalIndividualStartTime.competitorClassSection.id,
        }),
        {
            state: competitorState.value,
            state_reason: competitorStateReason.value,
        },
        {
            preserveScroll: true,
            onFinish: (visit) => {
                closeModals();
            },
        },
    );
}

const isRouterLoading = ref(false);
const generatePlannedStartTimeSectionId = ref(null);

import {
    startLocalEventTimeTicker,
    stopLocalEventTimeTicker,
    device_ts_ms_offset,
} from "@/Helpers/localEventTimeTicker.js";
import EventClassesFilter from "@/Pages/EventPublic/Components/EventClassesFilter.vue";
import EventSectionsFilter from "@/Pages/EventPublic/Components/EventSectionsFilter.vue";
import { toEventLocalISOString } from "@/Helpers/helperFunctions";
import FullscreenIcon from "@/Components/assets/FullscreenIcon.vue";
import FwbTooltip from "@/Components/FwbTooltip/FwbTooltip.vue";

let removeStartEventListenerStart;
let removeStartEventListenerFinish;

const startTimeGenOptions = [];
for (let i = 0, l = props.event.classes.length; i < l; i++) {
    for (let i2 = 0, l2 = props.event.classes[i].classSections.length; i2 < l2; i2++) {
        let firstStartAt = props.event.classes[i].classSections[i2].first_start_at;
        firstStartAt = DateTime.fromISO(firstStartAt)
            .setZone(props.event.timezone)
            .toLocaleString(DateTime.DATETIME_SHORT);
        startTimeGenOptions.push({
            sortkey: DateTime.fromISO(props.event.classes[i].classSections[i2].first_start_at)
                .setZone(props.event.timezone)
                .toFormat("X"),
            id: props.event.classes[i].classSections[i2].id,
            name: `${firstStartAt} @ ${props.event.classes[i].classSections[i2].name}: ${props.event.classes[i].name}`,
        });
    }
}

startTimeGenOptions.sort(function (a, b) {
    if (a.sortkey < b.sortkey) {
        return -1;
    }
    if (a.sortkey > b.sortkey) {
        return 1;
    }
    return 0;
});

onMounted(() => {
    // console.log("onMounted start list");
    // do not run local event time ticker in SSR
    if (typeof window !== "undefined") {
        if (usePage().props.can.view_sensitive_event) {
            startLocalEventTimeTicker(props.event.timezone);
        }
    }

    if (typeof window !== "undefined") {
        removeStartEventListenerStart = router.on("start", (event) => {
            // console.log(`Starting a visit to ${event.detail.visit.url}`);
            isRouterLoading.value = true;
        });

        removeStartEventListenerFinish = router.on("finish", (event) => {
            isRouterLoading.value = false;
        });
    }
    eventBus.$on("isCompetitorFormModalLoading", (val: boolean) => (isCompetitorFormModalLoading.value = val));
});

onUnmounted(() => {
    // console.log("onUnmounted start list");
    if (typeof window !== "undefined") {
        stopLocalEventTimeTicker();
    }

    eventBus.$off("isCompetitorFormModalLoading");

    if (typeof window !== "undefined") {
        removeStartEventListenerStart();
        removeStartEventListenerFinish();
    }
});
</script>

<template>
    <EventPublicWrapper>
        <Head>
            <title>Event start list: {{ $page.props.event.name }}</title>
            <meta
                property="og:title"
                head-key="og:title"
                :content="`Event start list: ${$page.props.meta_event_name}`"
            />
        </Head>
        <div class="max-w-7xl space-y-4 overflow-auto">
            <HeadingComponent class="rounded-lg bg-white p-4 shadow-lg sm:px-6">
                <template #name>Start list</template>
                <template #description>
                    <a
                        :href="
                            route('event.public.start-list-clock', {
                                hashid: event.hashid,
                                seo_slug: event.seo_slug,
                            })
                        "
                        >View in live fullscreen mode
                        <FullscreenIcon class="ml-1 inline size-3.5"></FullscreenIcon>
                    </a>
                </template>
            </HeadingComponent>

            <EventClassesFilter
                :start-list-class-selected="startListClassSelected"
                :start-list-section-selected="startListSectionSelected"
                :event-classes="event.classes"
                @set-start-list-class-selected="
                    (val) => {
                        startListClassSelected = val;
                    }
                "
                @set-start-list-section-selected="
                    (val) => {
                        startListSectionSelected = val;
                    }
                "
            ></EventClassesFilter>

            <EventSectionsFilter
                :start-list-class-selected="startListClassSelected"
                :start-list-section-selected="startListSectionSelected"
                :event-class-sections="event.classSections"
                @set-start-list-section-selected="
                    (val) => {
                        startListSectionSelected = val;
                    }
                "
            ></EventSectionsFilter>

            <HeadingComponent
                v-if="$page.props.can.view_sensitive_event"
                class="rounded-lg bg-orange-50 p-4 shadow-lg sm:px-6"
            >
                <template #name>Start list administration</template>
                <template #description>
                    <span v-if="event.is_ended"
                        >No administration tools available because event is already ended.</span
                    >
                    <span v-if="props.event.classSections.length === 0"
                        >No administration tools are available because the itinerary has not been configured.
                        <br />
                        <br />
                        <Button
                            :href="
                                route('event.edit.itinerary', {
                                    hashid: $page.props.event.hashid,
                                    seo_slug: $page.props.event.seo_slug,
                                })
                            "
                            class="h-6"
                            variant="outline"
                            ><PencilIcon
                                class="-ml-0.5 mr-1.5 size-4"
                                aria-hidden="true"
                            />
                            Configure itinerary</Button
                        >
                    </span>
                    <div
                        v-if="
                            !event.is_ended &&
                            startListClassSelected === null &&
                            startListSectionSelected === null &&
                            props.event.classSections.length > 0
                        "
                        class="space-x-4 space-y-4"
                    >
                        <Button @click="showSetStartTimesModal()"> Open start times generator</Button>
                        <a href="?excel=1">Download Excel</a>
                    </div>
                </template>
            </HeadingComponent>

            <div class="flex">
                <div class="flex-1 overflow-auto rounded border border-gray-200 bg-white shadow">
                    <template
                        v-for="eventClass in getCompetitorsInClass(startListClassSelected)"
                        :key="'eventid' + eventClass.id"
                    >
                        <hr class="border-4 border-gray-300" />
                        <div
                            v-for="(section, sectionIndex) in getSections(eventClass, startListSectionSelected)"
                            :key="'secid' + section.id"
                        >
                            <div
                                v-if="section.competitorClassSections.length === 0"
                                class="mb-4 flex rounded-lg bg-yellow-50 p-4 text-sm text-yellow-800"
                                role="alert"
                            >
                                <div class="flex items-center">
                                    <svg
                                        class="me-2 size-4 shrink-0"
                                        aria-hidden="true"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                                        />
                                    </svg>
                                    <span class="sr-only">Info</span>
                                    <h3 class="text-lg font-medium">
                                        Class "{{ eventClass.name }}": There are no accepted competitors in
                                        section "{{ section.name }}"
                                    </h3>
                                </div>
                                <div class="mt-2 text-sm"></div>
                            </div>
                            <table
                                v-if="section.competitorClassSections.length"
                                class="w-full table-auto"
                            >
                                <thead>
                                    <tr class="border-b bg-gray-50">
                                        <th
                                            class="p-4 text-left"
                                            colspan="6"
                                        >
                                            {{ section.name }}
                                            <span class="text-sm font-normal"
                                                >({{ $page.props.app.section_types[section.type].name }})</span
                                            >
                                            <br />
                                            {{ eventClass.name }}
                                            <span class="text-sm font-normal">(class)</span><br />
                                            <div class="font-normal">
                                                {{
                                                    DateTime.fromISO(section.first_start_at)
                                                        .setZone($page.props.event.timezone)
                                                        .toLocaleString(DateTime.DATETIME_SHORT)
                                                }}
                                                (first competitor's planned start at DSS)
                                            </div>
                                        </th>
                                    </tr>
                                    <tr>
                                        <th
                                            scope="col"
                                            class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                        >
                                            Number
                                        </th>
                                        <th
                                            scope="col"
                                            class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                        >
                                            Start time at DSS
                                            <span v-if="section.start_type === 'rolling_open'">
                                                (Open Rolling Start
                                                <TooltipIcon>
                                                    <div class="max-w-md">
                                                        Can start at any time after the "first competitor's
                                                        planned start at DSS". The actual start time at the DSS
                                                        will be identified automatically by the competitor's
                                                        GPS.
                                                    </div> </TooltipIcon
                                                >)
                                            </span>
                                            <span v-if="section.start_type === 'rolling_window'">
                                                (Rolling Start with Time Window
                                                <TooltipIcon>
                                                    <div>
                                                        Can start at the given time window under Event "Start
                                                        list" tab. The actual start time at the DSS will be
                                                        identified automatically by the competitor's GPS.
                                                    </div> </TooltipIcon
                                                >)
                                            </span>
                                        </th>
                                        <th
                                            scope="col"
                                            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                            &nbsp;
                                        </th>
                                        <th
                                            scope="col"
                                            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                            Competitor
                                        </th>
                                        <th
                                            scope="col"
                                            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                            Vehicle
                                        </th>
                                    </tr>
                                </thead>
                                <tbody class="bg-white">
                                    <template
                                        v-for="(
                                            competitorClassSection, competitorIndex
                                        ) in section.competitorClassSections"
                                        :key="'compidsub' + competitorClassSection.competitor.id"
                                    >
                                        <tr
                                            :class="[
                                                competitorIndex === 0 ? 'border-gray-300' : 'border-gray-200',
                                                'border-t',
                                                $page.props.user?.id ===
                                                competitorClassSection.competitor.user_id
                                                    ? 'font-bold'
                                                    : '',
                                            ]"
                                        >
                                            <td
                                                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-bold text-orange-400 sm:pl-6"
                                            >
                                                {{ competitorClassSection.competitor.competitor_number ?? "-" }}
                                            </td>
                                            <td
                                                class="py-4 pl-4 pr-3 text-sm tabular-nums text-gray-900 sm:pl-6"
                                                :class="
                                                    section.start_type === 'rolling_window'
                                                        ? 'whitespace-normal'
                                                        : 'whitespace-nowrap'
                                                "
                                            >
                                                <!--                                                start time-->
                                                <span v-if="section.start_type === 'rolling_open'">
                                                    {{
                                                        getFormattedPlannedStartTime(
                                                            section,
                                                            competitorClassSection,
                                                        )
                                                    }}
                                                </span>
                                                <FwbTooltip
                                                    v-else
                                                    :trigger="
                                                        getFormattedPlannedStartTime(
                                                            section,
                                                            competitorClassSection,
                                                        ) === '-'
                                                            ? null
                                                            : 'hover'
                                                    "
                                                >
                                                    <template #trigger>
                                                        <div>
                                                            <component
                                                                :is="`span`"
                                                                :class="
                                                                    $page.props.can.update_event
                                                                        ? 'cursor-pointer text-blue-600 group-hover:text-blue-900'
                                                                        : ''
                                                                "
                                                                @click.prevent="
                                                                    showModalIndividualStartTime(
                                                                        section,
                                                                        competitorClassSection,
                                                                    )
                                                                "
                                                            >
                                                                {{
                                                                    getFormattedPlannedStartTime(
                                                                        section,
                                                                        competitorClassSection,
                                                                    )
                                                                }}
                                                                <svg
                                                                    v-if="
                                                                        $page.props.can.update_event &&
                                                                        section.start_type !== 'rolling_open'
                                                                    "
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    class="inline size-5 text-blue-600 group-hover:text-blue-900"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    stroke="currentColor"
                                                                    stroke-width="2"
                                                                >
                                                                    <path
                                                                        stroke-linecap="round"
                                                                        stroke-linejoin="round"
                                                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                                    />
                                                                </svg>
                                                            </component>
                                                        </div>
                                                    </template>
                                                    <template #content>
                                                        <div
                                                            v-if="section.start_type === 'standing'"
                                                            class="max-w-sm"
                                                        >
                                                            {{
                                                                DateTime.fromISO(
                                                                    competitorClassSection.planned_start_at,
                                                                )
                                                                    .setZone($page.props.event.timezone)
                                                                    .toLocaleString(
                                                                        DateTime.DATETIME_SHORT_WITH_SECONDS,
                                                                    )
                                                            }}
                                                        </div>
                                                        <div
                                                            v-else
                                                            class="max-w-sm"
                                                        >
                                                            Actual start at:
                                                            <span v-if="competitorClassSection.start_at">
                                                                {{
                                                                    DateTime.fromISO(
                                                                        competitorClassSection.start_at,
                                                                    )
                                                                        .setZone($page.props.event.timezone)
                                                                        .toLocaleString(
                                                                            DateTime.DATETIME_SHORT_WITH_SECONDS,
                                                                        )
                                                                }}
                                                            </span>
                                                        </div>
                                                        <span
                                                            v-if="
                                                                competitorClassSection.start_at &&
                                                                competitorClassSection.meters > 1000
                                                            "
                                                            class="text-green-400"
                                                            >Started!</span
                                                        >
                                                        <span
                                                            v-else
                                                            class="text-red-400"
                                                            >Not started!</span
                                                        >
                                                    </template>
                                                </FwbTooltip>
                                                <FwbTooltip>
                                                    <template #trigger>
                                                        <div>{{ competitorClassSection.state }}</div>
                                                    </template>
                                                    <template #content>
                                                        {{ competitorClassSection.state_reason }}
                                                    </template>
                                                </FwbTooltip>
                                            </td>
                                            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                <!--                                                flag-->
                                                <span
                                                    :title="
                                                        $page.props.app.countries[
                                                            competitorClassSection.competitor.driver_country.toLowerCase()
                                                        ].name
                                                    "
                                                    :class="`border text-xl fi fi-${competitorClassSection.competitor.driver_country.toLowerCase()}`"
                                                ></span>
                                                <span
                                                    v-if="competitorClassSection.competitor.co_driver_country"
                                                    :title="
                                                        $page.props.app.countries[
                                                            competitorClassSection.competitor.co_driver_country.toLowerCase()
                                                        ].name
                                                    "
                                                    :class="`border text-xl fi fi-${competitorClassSection.competitor.co_driver_country.toLowerCase()}`"
                                                ></span>
                                            </td>
                                            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                <!--                                                competitor-->
                                                <span
                                                    v-if="$page.props.can.view_sensitive_event"
                                                    class="cursor-pointer text-blue-600"
                                                    @click="editCompetitor(competitorClassSection.competitor)"
                                                >
                                                    {{ competitorClassSection.competitor.driver_full_name }}
                                                    <template
                                                        v-if="
                                                            competitorClassSection.competitor
                                                                .co_driver_full_name
                                                        "
                                                    >
                                                        <br />{{
                                                            competitorClassSection.competitor
                                                                .co_driver_full_name
                                                        }}
                                                    </template>
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        class="inline size-5 text-blue-600 group-hover:text-blue-900"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                        stroke-width="2"
                                                    >
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                        />
                                                    </svg>
                                                </span>
                                                <template v-else>
                                                    {{ competitorClassSection.competitor.driver_full_name }}
                                                    <template
                                                        v-if="
                                                            competitorClassSection.competitor
                                                                .co_driver_full_name
                                                        "
                                                    >
                                                        <br />{{
                                                            competitorClassSection.competitor
                                                                .co_driver_full_name
                                                        }}
                                                    </template>
                                                </template>
                                                <span
                                                    v-if="
                                                        competitorClassSection.competitor.user &&
                                                        competitorClassSection.competitor.user.email
                                                    "
                                                >
                                                    <br />
                                                    {{ competitorClassSection.competitor.user.name }} <br />
                                                    {{ competitorClassSection.competitor.user.email }}
                                                </span>
                                            </td>
                                            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                <!--                                                vehicle-->
                                                <template v-if="competitorClassSection.competitor.team_name">
                                                    {{ competitorClassSection.competitor.team_name }} <br />
                                                </template>
                                                {{ competitorClassSection.competitor.vehicle_name }}
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <dialog-modal
            :show="modalStartTimes.show"
            @close="closeModals()"
        >
            <template #title> Start times generator</template>

            <template #content>
                <div class="mt-4 space-y-3">
                    <p
                        v-if="event.is_started"
                        class="text-red-500"
                    >
                        The event has started! Are you sure you want to regenerate the start times?<br />
                    </p>
                    <p class="text-red-500">
                        <strong
                            >This action will set/overwrite the competitors' start times for the section and
                            cannot be undone!</strong
                        >
                    </p>
                    <p>
                        The first competitor's start time at DSS will follow the planned start time for the
                        section's first competitor, as listed in the
                        <Link
                            :href="
                                route('event.edit.itinerary', {
                                    hashid: $page.props.event.hashid,
                                    seo_slug: $page.props.event.seo_slug,
                                })
                            "
                            >Event Itinerary
                        </Link>
                        <i class="text-sm">("The first competitor is planned to start at DSS")</i>
                        <br />
                        <br />
                        Start times will not be generated for competitors with any disabling state (e.g., DNS,
                        DNF, DNC, DIS) in the section.
                        <br />
                        <br />
                        You can regenerate start times multiple times if needed.<br />
                    </p>
                    <hr />
                    <div class="flex-1">
                        <div class="mt-4">
                            <FormInput
                                v-model="generatePlannedStartTimeSectionId"
                                class="mt-4"
                                type="select"
                                label="Section"
                                :options="startTimeGenOptions"
                            />
                        </div>
                    </div>

                    <div class="mt-4">
                        <jet-label
                            for="starting_interval"
                            value="Start interval (in minutes)"
                        />
                        <select
                            ref="starting_interval"
                            name="starting_interval"
                            class="mt-1 block w-3/4 rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        >
                            <option
                                v-for="i in [].length = 11"
                                :value="i - 1"
                                :selected="i - 1 === 1"
                            >
                                {{ i - 1 }} <span v-if="i - 1 === 0">(equal start time for all)</span>
                            </option>
                        </select>
                    </div>

                    <div class="pt-4 font-semibold">Order start times by:</div>

                    <div class="mt-4 flex flex-col space-y-3">
                        <div>
                            <input
                                id="by_competitor_numbers"
                                v-model="startOrderType"
                                type="radio"
                                value="by_competitor_numbers"
                            />
                            <label for="by_competitor_numbers"> Competitor numbers</label>
                        </div>

                        <div>
                            <input
                                id="by_competitor_numbers_reverse"
                                v-model="startOrderType"
                                type="radio"
                                value="by_competitor_numbers_reverse"
                            />
                            <label for="by_competitor_numbers_reverse"> Competitor numbers (reverse)</label>
                        </div>

                        <div>
                            <input
                                id="by_registration_order"
                                v-model="startOrderType"
                                type="radio"
                                value="by_registration_order"
                            />
                            <label for="by_registration_order"> Registration order</label>
                        </div>
                        <div>
                            <input
                                id="by_registration_order_reverse"
                                v-model="startOrderType"
                                type="radio"
                                value="by_registration_order_reverse"
                            />
                            <label for="by_registration_order_reverse"> Registration order (reverse)</label>
                        </div>

                        <div>
                            <input
                                id="by_prev_section_results_fastest"
                                v-model="startOrderType"
                                type="radio"
                                value="by_prev_section_results_fastest"
                            />
                            <label for="by_prev_section_results_fastest">
                                Previous section results (fastest first)</label
                            >
                        </div>

                        <div>
                            <input
                                id="by_prev_section_results_slowest"
                                v-model="startOrderType"
                                type="radio"
                                value="by_prev_section_results_slowest"
                            />
                            <label for="by_prev_section_results_slowest">
                                Previous section results (slowest first)</label
                            >
                        </div>
                    </div>
                </div>
            </template>

            <template #footer>
                <Button
                    color="white"
                    @click="closeModals()"
                >
                    Cancel
                </Button>
                <jet-button
                    class="ml-3"
                    :class="{ 'opacity-25': isRouterLoading || !generatePlannedStartTimeSectionId }"
                    :disabled="isRouterLoading || !generatePlannedStartTimeSectionId"
                    @click="regenerateStartTimesForSingleClassSection($refs.starting_interval.value)"
                >
                    <span> Generate start times </span>
                </jet-button>
            </template>
        </dialog-modal>

        <dialog-modal
            :show="modalIndividualStartTime.show"
            @close="closeModals()"
        >
            <template #title
                >Set competitor start time at DSS waypoint
                {{ modalIndividualStartTime.section.start_type === "rolling_window" ? "(planned)" : "" }}
            </template>

            <template #content>
                <div class="mt-4">
                    <div class="flex-1">
                        <div class="mt-4">
                            <div
                                v-if="modalIndividualStartTime.section.start_type === 'rolling_window'"
                                class="text-sm"
                            >
                                *This is a "Rolling Start with Time Window" section. <br />
                                You are only able to set the start time of the window. Editing the actual start
                                time is not yet possible.
                            </div>
                            <div class="my-4">
                                <p class="text-2xl">Local Event Time:</p>
                                <p class="text-7xl font-bold">
                                    <LocalEventTime
                                        :show-u-t-c="false"
                                        luxon-preset="TIME_24_WITH_SECONDS"
                                    />
                                </p>
                            </div>
                            <p>
                                {{
                                    $page.props.app.section_types[modalIndividualStartTime.section.type].name
                                }}:<br />
                                {{ modalIndividualStartTime.section.name }}
                            </p>
                            <br />
                            <p>
                                #<span class="font-bold">{{
                                    modalIndividualStartTime.competitorClassSection.competitor
                                        .competitor_number ?? "-"
                                }}</span>
                                <br />
                                <span class="font-bold"
                                    >{{
                                        modalIndividualStartTime.competitorClassSection.competitor
                                            .driver_full_name
                                    }}
                                    <template
                                        v-if="
                                            modalIndividualStartTime.competitorClassSection.competitor
                                                .co_driver_full_name
                                        "
                                    >
                                        <br />{{
                                            modalIndividualStartTime.competitorClassSection.competitor
                                                .co_driver_full_name
                                        }}
                                    </template>
                                </span>
                            </p>
                            <br />
                            <jet-label
                                for="datetimeIndividualStartTime"
                                value="Start time at DSS"
                            />
                            <div class="isolate -space-y-px rounded-md shadow-sm md:w-2/3">
                                <div
                                    class="relative rounded-md rounded-b-none px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-indigo-600"
                                >
                                    <input
                                        id="datetimeIndividualStartTime"
                                        ref="datetimeIndividualStartTime"
                                        v-model="startTimeModalInputValue"
                                        type="datetime-local"
                                        class="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0"
                                    />
                                </div>
                                <div
                                    class="relative rounded-md rounded-t-none px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-indigo-600"
                                >
                                    <input
                                        v-model="startTimeModalInputValue"
                                        type="text"
                                        class="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0"
                                    />
                                </div>
                            </div>

                            <div class="flex justify-between md:w-2/3">
                                <secondary-button @click="setStartCompetitorAtToNextMinute()">
                                    Set next minute
                                </secondary-button>
                                <jet-button
                                    :class="{ 'opacity-25': isRouterLoading }"
                                    :disabled="isRouterLoading"
                                    @click="setIndividualStartTime($refs.datetimeIndividualStartTime.value)"
                                >
                                    <span>
                                        Save
                                        {{
                                            modalIndividualStartTime.section.start_type === "rolling_window"
                                                ? " planned "
                                                : ""
                                        }}
                                        start time at DSS
                                    </span>
                                </jet-button>
                            </div>

                            <br />
                            <br />

                            <div class="border-b py-4 md:w-2/3">
                                Competitor state in this section:
                                <select
                                    id="state"
                                    ref="state"
                                    v-model="competitorState"
                                    name="state"
                                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                >
                                    <option
                                        value=""
                                        :selected="competitorState === null"
                                    >
                                        &nbsp;
                                    </option>
                                    <option
                                        value="DNS"
                                        :selected="competitorState === 'DNS'"
                                    >
                                        DNS (Did Not Start)
                                    </option>
                                    <option
                                        value="DNF"
                                        :selected="competitorState === 'DNF'"
                                    >
                                        DNF (Did Not Finish)
                                    </option>
                                    <option
                                        value="DNC"
                                        :selected="competitorState === 'DNC'"
                                    >
                                        DNC (Not classified)
                                    </option>
                                    <option
                                        value="DIS"
                                        :selected="competitorState === 'DIS'"
                                    >
                                        DIS (Disqualified)
                                    </option>
                                </select>
                                State reason (optional):
                                <input
                                    v-model="competitorStateReason"
                                    type="text"
                                    class="inline w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                />
                                <div class="flex justify-end">
                                    <jet-button
                                        :class="{ 'opacity-25': isRouterLoading }"
                                        :disabled="isRouterLoading"
                                        @click="saveCompetitorState()"
                                    >
                                        <span> Save state </span>
                                    </jet-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <template #footer>
                <Button
                    color="white"
                    @click="closeModals()"
                >
                    Cancel
                </Button>
            </template>
        </dialog-modal>
    </EventPublicWrapper>
</template>
